import { Popconfirm, message } from 'antd';
import { SelectProps } from 'rc-select/lib/Select';
import React, { useMemo } from 'react';

import Icon from '@ant-design/icons';
import {
  useDeleteDataTransform,
  usePostDataTransform,
  usePresetList,
  useUpdateDataTransform,
} from '@api/emp/queryHooks';
import ButtonKit from '@components/common/ButtonKit';
import TransformNameBuilder from '@components/data-transform-panel/components/TransformNameBuilder';
import TransformRuleBuilder from '@components/data-transform-panel/components/TransformRuleBuilder';
import {
  ITransformActiveIndices,
  ITransformItemActiveIndices,
  useDataTransformPanelStore,
} from '@components/data-transform-panel/store';
import {
  Header,
  HeaderTitleName,
  TitleSelect,
  TransformRulesWrapper,
  Wrapper,
} from '@components/data-transform-panel/styled';
import { ETransformRuleActionType } from '@components/data-transform-panel/typed';
import {
  addAndTransformMetadataItem,
  addOrTransformMetadataItem,
  addTransformMetadataGroup,
  deleteTransformMetadataGroup,
  deleteTransformMetadataItem,
  trimTransformRules,
  updateTransformMetadataByLogicOperator,
  updateTransformRules,
} from '@components/data-transform-panel/utils/handleTransformRulesUtils';
import {
  getEmptyTransformRuleGroup,
  getInitialActiveIndices,
} from '@components/data-transform-panel/utils/initializeUtils';
import { validateTransformData } from '@components/data-transform-panel/utils/validateUtils';
import { useDimensionPanelStore } from '@components/dimensions/store';
import { EInputType } from '@components/mapping-panel/components/RuleBuilder/KeyValueBuilder/typed';
import { useMappingPanelStore } from '@components/mapping-panel/store';
import CancelIcon from '@icons/emp/cancel.svg';
import SaveIcon from '@icons/emp/save.svg';
import DataTransformIcon from '@icons/rename-page/data-transform.svg';
import { renamePresetDimensionMap } from '@src/common/constant/emp';
import {
  EDimensionType,
  ERenamePreset,
  ERenameTitle,
  ERenamedDimension,
} from '@src/common/constant/enum';
import { ITransformData } from '@src/common/types/empTypes';
import PermissionButton from '@src/emp-3.0/components/PermissionButton';

const titleOptions = [
  {
    value: ERenameTitle.PAGE,
    label: ERenameTitle.PAGE,
  },
  {
    value: ERenameTitle.SCREEN,
    label: ERenameTitle.SCREEN,
  },
];

const titlePresetMap = {
  [ERenameTitle.PAGE]: ERenamePreset.PAGE,
  [ERenameTitle.SCREEN]: ERenamePreset.SCREEN,
};

const titleTargetDimensionMap = {
  [ERenameTitle.PAGE]: renamePresetDimensionMap[ERenamePreset.PAGE],
  [ERenameTitle.SCREEN]: renamePresetDimensionMap[ERenamePreset.SCREEN],
};

const DataTransformPanel = () => {
  const [
    transformActiveIndices,
    transformId,
    transformTitle,
    transformName,
    transformTargetDimension,
    transformRule,
    setTransformActiveIndices,
    setTransformPanelOpen,
    setTransformId,
    setTransformTitle,
    setTransformName,
    setTransformTargetDimension,
    setTransformRule,
  ] = useDataTransformPanelStore((state) => [
    state.transformActiveIndices,
    state.transformId,
    state.transformTitle,
    state.transformName,
    state.transformTargetDimension,
    state.transformRule,
    state.setTransformActiveIndices,
    state.setTransformPanelOpen,
    state.setTransformId,
    state.setTransformTitle,
    state.setTransformName,
    state.setTransformTargetDimension,
    state.setTransformRule,
  ]);
  const [setCurPresetName] = useDimensionPanelStore((state) => [state.setCurPresetName]);
  const [setNeedRefetchQueryKeys] = useMappingPanelStore((state) => [
    state.setNeedRefetchQueryKeys,
  ]);

  const { mutate: createDataTransform, isLoading: isCreateLoading } = usePostDataTransform();
  const { mutate: updateDataTransform, isLoading: isUpdateLoading } = useUpdateDataTransform();
  const { mutate: deleteDataTransform, isLoading: isDeleteLoading } = useDeleteDataTransform();
  const { data: presetList } = usePresetList();

  const isUpdateMode = !!transformId;

  const modifyNeedRefetchQueryFlag = () => {
    transformTargetDimension && setNeedRefetchQueryKeys(transformTargetDimension);
  };

  const handleReset = () => {
    setTransformId();
    setTransformName();
    setTransformTitle(ERenameTitle.PAGE);
    setTransformTargetDimension(ERenamedDimension.edited_page_title);
    setTransformActiveIndices(getInitialActiveIndices());
    setTransformRule([getEmptyTransformRuleGroup()]);
  };

  const handleCreateDataTransform = (res: any) => {
    createDataTransform(res.transformData, {
      onSuccess: () => {
        message.success('create success!');
        handleReset();
        setTransformPanelOpen(false);
        modifyNeedRefetchQueryFlag();
      },
      onError: (error: any) => {
        message.error(error?.response?.data?.msg);
      },
    });
  };

  const handleUpdateDataTransform = (res: any) => {
    updateDataTransform(
      { nameId: res.nameId, queryBody: res.transformData },
      {
        onSuccess: () => {
          message.success('update success!');
          handleReset();
          setTransformPanelOpen(false);
          modifyNeedRefetchQueryFlag();
        },
        onError: (error: any) => {
          message.error(error?.response?.data?.msg);
        },
      },
    );
  };

  const handleDeleteDataTransform = (nameId: any) => {
    deleteDataTransform(
      { nameId },
      {
        onSuccess: () => {
          message.success('Delete success!');
          handleReset();
          setTransformPanelOpen(false);
          modifyNeedRefetchQueryFlag();
        },
        onError: (error: any) => {
          message.error(error?.response?.data?.msg);
        },
      },
    );
  };

  const updateTransformActiveIndices = (
    orIndex?: number,
    metadataNameActive?: boolean,
    ruleItems?: ITransformItemActiveIndices[],
  ) => {
    setTransformActiveIndices({ orIndex, metadataNameActive, ruleItems });
  };

  const handleChange = (
    orIndex: number,
    subAndIndex: number,
    subOrIndex: number,
    type: EInputType,
    value: string,
  ) => {
    setTransformRule([
      ...updateTransformRules(transformRule, orIndex, subAndIndex, subOrIndex, type, value),
    ]);
  };

  const handleRulesChange = (
    actionType: ETransformRuleActionType,
    orIndex = 0,
    subAndIndex = 0,
    subOrIndex?: number,
  ) => {
    let rule = transformRule;
    switch (actionType) {
      case ETransformRuleActionType.DELETE_ALL_TRANSFORM:
        rule = [getEmptyTransformRuleGroup()];
        break;
      case ETransformRuleActionType.DELETE_TRANSFORM_METADATA_GROUP:
        rule = [...deleteTransformMetadataGroup(rule, orIndex)];
        break;
      case ETransformRuleActionType.DELETE_TRANSFORM_METADATA_ITEM:
        rule = [...deleteTransformMetadataItem(rule, orIndex, subAndIndex, subOrIndex ?? 0)];
        break;
      case ETransformRuleActionType.ADD_TRANSFORM_METADATA_GROUP:
        rule = [...addTransformMetadataGroup(rule)];
        break;
      case ETransformRuleActionType.ADD_AND_TRANSFORM_METADATA_ITEM:
        rule = [...addAndTransformMetadataItem(rule, orIndex)];
        break;
      case ETransformRuleActionType.ADD_OR_TRANSFORM_METADATA_ITEM:
        rule = [...addOrTransformMetadataItem(rule, orIndex)];
        break;
      case ETransformRuleActionType.LOGIC_OPERATOR_CHANGE:
        rule = [...updateTransformMetadataByLogicOperator(rule, orIndex, subAndIndex, subOrIndex)];
        break;
      default:
        break;
    }
    setTransformRule(rule);
  };

  const handleCancel = () => {
    setTransformPanelOpen(false);
    handleReset();
    setCurPresetName('Explore');
  };

  const handleSave = () => {
    const uncheckTransformData = {
      dimension_value: transformName,
      target_dimension: transformTargetDimension,
      transform_rule: [...trimTransformRules(transformRule)],
    };
    const res = validateTransformData(uncheckTransformData as ITransformData);
    if (!res.status) {
      message.warning(`${res.key?.join(' OR ')} ${res.result}`).then();
      return;
    }
    isUpdateMode
      ? handleUpdateDataTransform({ nameId: transformId, ...res })
      : handleCreateDataTransform(res);
  };

  const handleDelete = () => {
    handleDeleteDataTransform(transformId);
  };

  const handleDragEnd = (result) => {
    const { destination, source } = result;

    if (
      !destination ||
      (destination.droppableId === source.droppableId && destination.index === source.index)
    ) {
      return;
    }

    const rules = transformRule;

    try {
      const destinationObj = JSON.parse(destination.droppableId);
      const sourceObj = JSON.parse(source.droppableId);
      const { orIndex, subAndIndex: destinationSubAndIndex } = destinationObj;
      const { subAndIndex: sourceSubAndIndex } = sourceObj;
      const sourceIndex = source.index;
      const destinationIndex = destination.index;

      const temp = rules[orIndex].rule[sourceSubAndIndex].splice(sourceIndex, 1);
      rules[orIndex].rule[destinationSubAndIndex].splice(destinationIndex, 0, ...temp);

      setTransformRule([...rules]);

      const activeIndicesProps: ITransformActiveIndices = {
        orIndex,
        ruleItems: [
          {
            subAndIndex: destinationSubAndIndex,
            subOrIndex: destinationIndex,
          },
        ],
      };

      setTransformActiveIndices(activeIndicesProps);
    } catch (e) {
      console.log('Drag drop end error: ', e);
    }
  };

  const handleTitleChange = (value) => {
    setTransformTitle(value);
    setCurPresetName(titlePresetMap[value]);
    setTransformTargetDimension(titleTargetDimensionMap[value]);
    setTransformRule([getEmptyTransformRuleGroup()]);
  };

  const metadataNameOptions = useMemo(() => {
    let options: SelectProps['options'] = [];
    if (transformTitle && presetList?.data) {
      (presetList?.data ?? []).forEach((dataSource) => {
        if (dataSource.preset_name === titlePresetMap[transformTitle]) {
          options = (dataSource.dimension_list ?? [])
            ?.filter((dimension) => dimension.dimension_type === EDimensionType.EVENT_ATTRIBUTE)
            ?.map((dimension) => ({
              value: dimension.dimension_name,
              label: dimension.display_name,
            }));
        }
      });
    }
    return options;
  }, [presetList, transformTitle]);

  return (
    <Wrapper>
      <Header>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
          }}
        >
          <Icon component={DataTransformIcon} />
          <HeaderTitleName>Data Transform:</HeaderTitleName>
          <TitleSelect
            disabled={isUpdateMode}
            value={transformTitle}
            bordered={false}
            options={titleOptions}
            onChange={handleTitleChange}
          />
        </div>
        <div style={{ display: 'flex', gap: '8px' }}>
          {isUpdateMode ? (
            <>
              <PermissionButton tooltip="" onConfirm={handleDelete}>
                <Popconfirm
                  title="The deleted data-transform cannot be restored. Do you want to continue?"
                  icon={false}
                  okText="Delete"
                  cancelButtonProps={{ type: 'default' }}
                  okButtonProps={{
                    danger: true,
                    type: 'ghost',
                    style: {
                      border: '1px solid #d0021b',
                      color: '#d0021b',
                    },
                  }}
                >
                  <ButtonKit
                    kitType="danger"
                    style={{
                      gap: '6px',
                      padding: '3px 11px',
                    }}
                    loading={isDeleteLoading}
                    disabled={isCreateLoading || isUpdateLoading}
                  >
                    Delete
                  </ButtonKit>
                </Popconfirm>
              </PermissionButton>
              <div
                style={{
                  width: '1px',
                  height: '24px',
                  background: '#D9D9D9',
                  margin: '0 7px',
                }}
              />
            </>
          ) : null}
          <PermissionButton tooltip="" onClick={handleSave}>
            <ButtonKit
              kitType="primary"
              prefix={<Icon component={SaveIcon} />}
              style={{
                gap: '6px',
                paddingRight: '11px',
              }}
              loading={isCreateLoading || isUpdateLoading}
              disabled={isDeleteLoading}
            >
              Save
            </ButtonKit>
          </PermissionButton>
          <ButtonKit
            prefix={<Icon component={CancelIcon} />}
            onClick={handleCancel}
            style={{
              gap: '6px',
              paddingRight: '11px',
            }}
            disabled={isDeleteLoading || isCreateLoading || isUpdateLoading}
          >
            Cancel
          </ButtonKit>
        </div>
      </Header>
      <TransformRulesWrapper>
        <div
          style={{
            display: 'flex',
            flexGrow: 1,
            flexDirection: 'column',
          }}
        >
          <TransformRuleBuilder
            activeIndices={transformActiveIndices}
            data={transformRule}
            updateActiveIndices={updateTransformActiveIndices}
            onChange={handleChange}
            onRulesChange={handleRulesChange}
            onDragEnd={handleDragEnd}
            metadataList={metadataNameOptions}
          />
        </div>
        <TransformNameBuilder
          name={transformName}
          targetDimension={transformTargetDimension}
          onInputChange={(e) => {
            const { value: inputValue } = e.target;
            setTransformName(inputValue);
          }}
        />
      </TransformRulesWrapper>
    </Wrapper>
  );
};

export default DataTransformPanel;
